import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { LockReset, VpnKey } from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function ChangePasswordDialog({ open, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin-root' || user?.role === 'admin-sub';

  // Login password section
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState(false);
  const [passLoading, setPassLoading] = useState(false);

  // Deploy key section (admin only)
  const [deployCurrentPassword, setDeployCurrentPassword] = useState('');
  const [deployKey, setDeployKey] = useState('');
  const [confirmDeployKey, setConfirmDeployKey] = useState('');
  const [deployError, setDeployError] = useState('');
  const [deploySuccess, setDeploySuccess] = useState(false);
  const [deployLoading, setDeployLoading] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPassError('');
    setPassSuccess(false);

    if (newPassword !== confirmPassword) {
      setPassError('Mật khẩu mới không khớp');
      return;
    }
    if (newPassword.length < 6) {
      setPassError('Mật khẩu mới phải có ít nhất 6 ký tự');
      return;
    }

    setPassLoading(true);
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      setPassSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPassError(err.response?.data?.error || 'Đổi mật khẩu thất bại');
    } finally {
      setPassLoading(false);
    }
  };

  const handleDeployKeySubmit = async (e) => {
    e.preventDefault();
    setDeployError('');
    setDeploySuccess(false);

    if (deployKey !== confirmDeployKey) {
      setDeployError('Xác nhận mật khẩu triển khai không khớp');
      return;
    }
    if (deployKey.length < 6) {
      setDeployError('Mật khẩu triển khai phải có ít nhất 6 ký tự');
      return;
    }

    setDeployLoading(true);
    try {
      await authAPI.setDeployKey({
        currentPassword: deployCurrentPassword,
        deployKey,
        confirmDeployKey,
      });
      setDeploySuccess(true);
      setDeployCurrentPassword('');
      setDeployKey('');
      setConfirmDeployKey('');
    } catch (err) {
      setDeployError(err.response?.data?.error || 'Cập nhật mật khẩu triển khai thất bại');
    } finally {
      setDeployLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPassError('');
    setPassSuccess(false);
    setDeployCurrentPassword('');
    setDeployKey('');
    setConfirmDeployKey('');
    setDeployError('');
    setDeploySuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cập nhật mật khẩu</DialogTitle>
      <DialogContent>
        {/* ── Login password ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LockReset fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>Mật khẩu đăng nhập</Typography>
        </Box>

        <form id="change-pass-form" onSubmit={handlePasswordSubmit}>
          {passError && <Alert severity="error" sx={{ mb: 1.5 }}>{passError}</Alert>}
          {passSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>Đổi mật khẩu thành công!</Alert>}

          <TextField
            margin="normal"
            required
            fullWidth
            type="password"
            label="Mật khẩu hiện tại"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            size="small"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            type="password"
            label="Mật khẩu mới"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            size="small"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            type="password"
            label="Xác nhận mật khẩu mới"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            size="small"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={passLoading}
            sx={{ mt: 1 }}
          >
            {passLoading ? 'Đang xử lý...' : 'Đổi mật khẩu'}
          </Button>
        </form>

        {/* ── Deploy key (admin only) ── */}
        {isAdmin && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VpnKey fontSize="small" color="warning" />
              <Typography variant="subtitle2" fontWeight={600}>Mật khẩu triển khai EA</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Dùng để xác nhận khi deploy file .mq5 qua trang Dashboard.
            </Typography>

            <form id="deploy-key-form" onSubmit={handleDeployKeySubmit}>
              {deployError && <Alert severity="error" sx={{ mb: 1.5 }}>{deployError}</Alert>}
              {deploySuccess && (
                <Alert severity="success" sx={{ mb: 1.5 }}>Cập nhật mật khẩu triển khai thành công!</Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                type="password"
                label="Mật khẩu đăng nhập hiện tại (để xác nhận)"
                value={deployCurrentPassword}
                onChange={(e) => setDeployCurrentPassword(e.target.value)}
                size="small"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                type="password"
                label="Mật khẩu triển khai mới"
                value={deployKey}
                onChange={(e) => setDeployKey(e.target.value)}
                size="small"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                type="password"
                label="Xác nhận mật khẩu triển khai"
                value={confirmDeployKey}
                onChange={(e) => setConfirmDeployKey(e.target.value)}
                size="small"
              />
              <Button
                type="submit"
                variant="outlined"
                color="warning"
                disabled={deployLoading}
                sx={{ mt: 1 }}
              >
                {deployLoading ? 'Đang xử lý...' : 'Cập nhật mật khẩu triển khai'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}
